import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { AlertTriangle, Coffee, Megaphone } from "lucide-react";

export function LatestAnnouncements() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/communications").then(({ data }) => setItems((data.items || []).filter((a) => a.status === "published").slice(0, 4))).catch(() => {});
  }, []);
  if (!items.length) return null;
  return (
    <Card className="p-5" data-testid="hrms-latest-announcements">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 font-display text-lg font-semibold"><Megaphone className="h-4 w-4" /> Latest announcements</div>
        <Link to="/communications" className="text-xs text-[#2f8f86] hover:underline" data-testid="hrms-announcements-view-all">View all</Link>
      </div>
      <div className="mt-3 divide-y divide-border">
        {items.map((a) => (
          <Link key={a.id} to={`/communications?announcement=${a.id}`} className="flex items-center justify-between gap-3 py-2 text-sm hover:text-[#2f8f86]" data-testid={`hrms-announcement-${a.id}`}>
            <span className="truncate">{!a.is_read && <span className="mr-2 inline-block h-2 w-2 rounded-full bg-[#2f8f86]" />}{a.title}</span>
            <span className="shrink-0 text-xs text-muted-foreground">{a.category}</span>
          </Link>
        ))}
      </div>
    </Card>
  );
}

export function TeamLivePanel() {
  const [data, setData] = useState(null);
  useEffect(() => {
    const load = () => api.get("/hrms/break-tracker").then(({ data: d }) => setData(d)).catch(() => {});
    load();
    const timer = setInterval(load, 15000);
    return () => clearInterval(timer);
  }, []);
  if (!data) return null;
  const onBreak = data.board.filter((r) => r.status === "on_break");
  const working = data.board.filter((r) => r.status === "working");
  const minutesSince = (iso) => Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
  return (
    <div className="space-y-3" data-testid="hrms-team-live">
      {data.alerts.map((a) => (
        <div key={a.user_id} className="flex items-center gap-2 rounded-md border border-red-500/40 bg-red-500/10 px-4 py-2 text-sm" data-testid={`hrms-over-limit-banner-${a.user_id}`}>
          <AlertTriangle className="h-4 w-4 text-red-600" /> {a.employee_name} is over the break limit ({a.duration_minutes} of {a.limit_minutes} min){a.running ? " — still on break" : ""}.
        </div>
      ))}
      <Card className="p-5">
        <div className="text-sm font-semibold">Live now · {working.length} working · {onBreak.length} on break</div>
        <div className="mt-2 flex flex-wrap gap-2">
          {data.on_break_now.map((p) => (
            <span key={p.user_id} className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/15 px-3 py-1 text-xs text-amber-700 dark:text-amber-300" data-testid={`hrms-live-break-${p.user_id}`}>
              <Coffee className="h-3.5 w-3.5" /> {p.employee_name} is on break ({minutesSince(p.since)} min)
            </span>
          ))}
          {working.map((p) => (
            <span key={p.user_id} className="rounded-full bg-emerald-500/15 px-3 py-1 text-xs text-emerald-700 dark:text-emerald-300">{p.employee_name} working</span>
          ))}
          {!onBreak.length && !working.length && <span className="text-xs text-muted-foreground">Nobody is clocked in right now.</span>}
        </div>
      </Card>
    </div>
  );
}
